import { Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { Text } from "../components/Text";
import { ChatFileMessage } from "./components/ChatFileMessage";
import { ChatSystemMessage } from "./components/ChatSystemMessage";
import { ChatTextMessage } from "./components/ChatTextMessage";
import { FcmRequestMessageRouter } from "./components/FcmRequestMessageRouter";
import { PlacementMessageRouter } from "./components/PlacementMessageRouter";
import { ShiftInviteMessageRouter } from "./components/ShiftInviteMessageRouter";
import { ShiftTimeProposalMessageRouter } from "./components/ShiftTimeProposalMessageRouter";
import { VisitInviteMessageRouter } from "./components/VisitInviteMessageRouter";
import { WorkerCredentialMessageRouter } from "./components/WorkerCredentialMessageRouter";
import { WorkerLockNotificationMessage } from "./components/WorkerLockNotificationMessage";
import {
  FCM_REQUEST_MESSAGE_TYPES,
  MESSAGE_CUSTOM_TYPES,
  type MessageCustomType,
  PLACEMENT_MESSAGE_TYPES,
  SHIFT_INVITE_MESSAGE_TYPES,
  SHIFT_TIME_PROPOSAL_MESSAGE_TYPES,
  SYSTEM_EVENT_MESSAGE_TYPES,
  VISIT_INVITE_MESSAGE_TYPES,
  WORKER_CREDENTIAL_MESSAGE_TYPES,
} from "./constants";
import type {
  ChatMessage as ChatMessageType,
  PlacementChannelMetadata,
  ReplyContext,
} from "./types";
import { parseCustomMessage } from "./utils/parseCustomMessage";

function isSystemEventMessage(customType: string): boolean {
  return SYSTEM_EVENT_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

function isPlacementMessage(customType: string): boolean {
  return PLACEMENT_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

function isShiftInviteMessage(customType: string): boolean {
  return SHIFT_INVITE_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

function isVisitInviteMessage(customType: string): boolean {
  return VISIT_INVITE_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

function isWorkerCredentialMessage(customType: string): boolean {
  return WORKER_CREDENTIAL_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

function isFcmRequestMessage(customType: string): boolean {
  return FCM_REQUEST_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

function isShiftTimeProposalMessage(customType: string): boolean {
  return SHIFT_TIME_PROPOSAL_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

export interface ChatMessageProps {
  message: ChatMessageType;
  isIncoming: boolean;
  onReply?: (context: ReplyContext) => void;
  /**
   * Channel URL in format: workplaceId_workerId_placementId
   * Required for placement message routing.
   */
  channelUrl?: string;
  /**
   * Channel metadata for placement-related chats.
   */
  channelMetadata?: PlacementChannelMetadata;
  /**
   * Worker name for credential messages.
   */
  workerName?: string;
  /**
   * Workplace ID for FCM request messages.
   */
  workplaceId?: string;
  /**
   * Workplace user ID for FCM request messages.
   */
  workplaceUserId?: string;
  sx?: SxProps<Theme>;
}

/**
 * Renders different message types (text, file, admin, system events) with support for replies
 */
// eslint-disable-next-line complexity
export function ChatMessage(props: ChatMessageProps) {
  const {
    message,
    isIncoming,
    onReply,
    channelUrl,
    channelMetadata,
    workerName,
    workplaceId,
    workplaceUserId,
    sx,
  } = props;

  // Placement messages need rich card UI with actions
  if (isPlacementMessage(message.customType) && message.type === "admin") {
    const placementMessage = (
      <PlacementMessageRouter
        message={message}
        channelUrl={channelUrl ?? ""}
        channelMetadata={channelMetadata}
        sx={sx}
      />
    );
    if (placementMessage) {
      return placementMessage;
    }
  }

  // Shift invite messages need rich card UI with status and actions
  if (isShiftInviteMessage(message.customType) && message.type === "admin") {
    const shiftInviteMessage = <ShiftInviteMessageRouter message={message} sx={sx} />;
    if (shiftInviteMessage) {
      return shiftInviteMessage;
    }
  }

  // Visit invite messages for Home Health workplaces
  if (isVisitInviteMessage(message.customType) && message.type === "admin") {
    const visitInviteMessage = <VisitInviteMessageRouter message={message} sx={sx} />;
    if (visitInviteMessage) {
      return visitInviteMessage;
    }
  }

  // Worker credential messages with modal integration
  if (isWorkerCredentialMessage(message.customType) && message.type === "admin") {
    const credentialMessage = (
      <WorkerCredentialMessageRouter
        message={message}
        workerName={workerName ?? channelMetadata?.hcpName}
        sx={sx}
      />
    );
    if (credentialMessage) {
      return credentialMessage;
    }
  }

  // FCM (Facility Canceled Me / Sent Home) request messages with action buttons
  if (isFcmRequestMessage(message.customType) && message.type === "admin" && workplaceId) {
    const fcmRequestMessage = (
      <FcmRequestMessageRouter
        message={message}
        workplaceId={workplaceId}
        workplaceUserId={workplaceUserId}
      />
    );
    if (fcmRequestMessage) {
      return fcmRequestMessage;
    }
  }

  // Shift time proposal messages with rich card UI showing proposed and original times
  if (isShiftTimeProposalMessage(message.customType) && message.type === "admin") {
    const shiftTimeProposalMessage = <ShiftTimeProposalMessageRouter message={message} sx={sx} />;
    if (shiftTimeProposalMessage) {
      return shiftTimeProposalMessage;
    }
  }

  // System event messages (check-ins, cancellations, etc.) are rendered with contextual colors
  if (isSystemEventMessage(message.customType)) {
    return (
      <ChatSystemMessage
        customType={message.customType}
        message={message.type === "admin" ? message.message : ""}
        sx={sx}
      />
    );
  }

  // Worker lock notification messages are rendered as outgoing text bubbles with preserved newlines
  if (
    message.customType === MESSAGE_CUSTOM_TYPES.WORKER_LOCK_NOTIFICATION &&
    message.type === "admin"
  ) {
    return <WorkerLockNotificationMessage message={message} sx={sx} />;
  }

  // Admin messages are always centered and styled differently
  if (message.type === "admin") {
    const displayMessage = parseCustomMessage(message.message);

    return (
      <Box
        sx={mergeSxProps(
          {
            display: "flex",
            justifyContent: "center",
            padding: 2,
          },
          sx
        )}
      >
        <Text
          variant="caption"
          sx={(theme) => ({
            color: theme.palette.base?.inactive,
            textAlign: "center",
          })}
        >
          {displayMessage}
        </Text>
      </Box>
    );
  }

  // File messages
  if (message.type === "file") {
    return <ChatFileMessage message={message} isIncoming={isIncoming} sx={sx} onReply={onReply} />;
  }

  // Text messages with optional parent message (reply)
  return <ChatTextMessage message={message} isIncoming={isIncoming} sx={sx} onReply={onReply} />;
}
